import { BodyLarge, BodyM, Button, Caption, colorVariants, Heading2, Heading4 } from '@casavo/base-ui'
import cn from 'classnames'
import useTranslation from 'next-translate/useTranslation'
import React, { MutableRefObject, useEffect } from 'react'

import Icon from '@/components/Icon'
import { useDevice } from '@/context/Device'
import { useModal } from '@/context/ModalContext'
import { sprinkles } from '@/design-lib/style-theme/sprinkles.css'
import { ListingCate } from '@/page-commons/listingBySlug'
import getTranslatedTitle from '@/utils/get-translated-title'
import { handleClick, handleShow } from '@/utils/tracking/eventsHandlers'

import { ConceptImage } from '../ConceptImage'
import { ModalBase } from '../Modal/ModalBase'
import { Translated } from '../Translated'

import { RequestType } from './index'

import { requestContactItem } from './styles.css'

type Props = {
  listing: ListingCate
  listingTitle: string
  requestType: RequestType
  similarPropertiesRef: MutableRefObject<HTMLDivElement>
}

export const RequestContactFeedback: React.FC<Props> = ({ listing, requestType, similarPropertiesRef }) => {
  const { closeModal } = useModal()
  const device = useDevice()
  const isVisitType = requestType === 'visit'
  const { lang, t } = useTranslation()

  useEffect(() => {
    handleShow(isVisitType ? 'BookVisitFeedbackModal' : 'BookCallFeedbackModal', 'Open')
  }, [])

  return (
    <ModalBase data-testid="request-feedback">
      <div className={sprinkles({ alignSelf: 'center', paddingBottom: 'm' })}>
        <ConceptImage height={106} name="phone" width={106} />
      </div>
      <Heading2 alignment="center" className={sprinkles({ paddingBottom: 'l' })} data-testid="request-feedback-title">
        <Translated
          components={{ 0: <span className={colorVariants.deepGreen600} /> }}
          i18Key={
            isVisitType
              ? t('listing-detail:request_visit_modal_thank_you_title')
              : t('listing-detail:request_call_modal_thank_you_title')
          }
        />
      </Heading2>
      <Caption alignment="center" className={sprinkles({ paddingBottom: 'xl' })}>
        {getTranslatedTitle(listing.detail?.titles, lang)}
      </Caption>

      <BodyLarge
        strong
        alignment="center"
        className={sprinkles({
          paddingBottom: 'l',
        })}
        data-testid="request-feedback-title-2"
      >
        {isVisitType
          ? t('listing-detail:request_visit_modal_thank_you_title_2')
          : t('listing-detail:request_call_modal_thank_you_title_2')}
      </BodyLarge>

      <div
        className={sprinkles({
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          paddingBottom: 'xl',
          paddingX: { mobile: 'm', tablet: '2xl' },
          rowGap: 'm',
          textAlign: 'left',
        })}
        role="list"
      >
        <div className={requestContactItem}>
          <Icon.Check className={cn(colorVariants.deepGreen600, sprinkles({ flexShrink: 0 }))} />
          <BodyM data-testid="request-feedback-bullet1">
            <Translated
              i18Key={
                isVisitType
                  ? 'listing-detail:request_visit_modal_thankyou_item_1'
                  : 'listing-detail:RequestCall_ThankYou_Bullet1'
              }
            />
          </BodyM>
        </div>
        <div className={requestContactItem}>
          <Icon.Check className={cn(colorVariants.deepGreen600, sprinkles({ flexShrink: 0 }))} />
          <BodyM data-testid="request-feedback-bullet2">
            <Translated
              i18Key={
                isVisitType
                  ? 'listing-detail:request_visit_modal_thankyou_item_2'
                  : 'listing-detail:request_feedback_call_bullet_2'
              }
            />
          </BodyM>
        </div>
      </div>

      <Button
        center
        className={sprinkles({ flexShrink: 0 })}
        fullWidth={device === 'mobile'}
        label={
          Boolean(similarPropertiesRef?.current)
            ? t('listing-detail:form-body.success_button')
            : t('listing-detail:form-body.success_button_alt')
        }
        minWidth={245}
        onClick={() => {
          if (!similarPropertiesRef?.current) {
            handleClick('RequestContactModalThankYou', 'ToListingPage')
            closeModal()
            return
          }

          handleClick('RequestContactModalThankYou', 'ToSimilarPropertiesFromRequestContactModalThankYou')
          closeModal()
          setTimeout(() => {
            similarPropertiesRef?.current.scrollIntoView({ behavior: 'smooth' })
          }, 500)
        }}
      />
    </ModalBase>
  )
}
